import React from 'react';
import {
  Box, Paper, Skeleton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Pagination, Typography,
} from '@mui/material';
import { surfaceTints } from './theme';

export interface DataTableColumn<Row> {
  key: string;
  label: string;
  render: (row: Row) => React.ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
}

interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => React.Key;
  loading?: boolean;
  skeletonRows?: number;
  emptyMessage?: React.ReactNode;
  /** Controlled sorting: the parent owns the sort (client- or server-side). */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (key: string) => void;
  onRowClick?: (row: Row) => void;
  /** Announced on row focus for keyboard/SR users; table semantics preserved over role="button". */
  rowAriaLabel?: (row: Row) => string;
  /** Controlled pagination: the parent owns the page slice. */
  page?: number;
  pageCount?: number;
  onPageChange?: (page: number) => void;
}

/** Dense read-only table: controlled sort + pagination, skeleton state, keyboard-activatable rows. */
function DataTable<Row>({
  columns, rows, rowKey, loading = false, skeletonRows = 10, emptyMessage = 'No data',
  sortBy, sortDir = 'asc', onSortChange, onRowClick, rowAriaLabel, page, pageCount, onPageChange,
}: DataTableProps<Row>) {
  const clickable = Boolean(onRowClick);
  return (
    <Box>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: surfaceTints.tableHeader }}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align ?? 'left'}
                  sx={{ minWidth: col.minWidth, fontWeight: 600, color: 'text.secondary',
                        fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.25 }}
                >
                  {col.sortable && onSortChange ? (
                    <TableSortLabel
                      active={sortBy === col.key}
                      direction={sortBy === col.key ? sortDir : 'asc'}
                      onClick={() => onSortChange(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: skeletonRows }, (_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align ?? 'left'}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 5, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  hover={clickable}
                  tabIndex={clickable ? 0 : undefined}
                  aria-label={clickable && rowAriaLabel ? rowAriaLabel(row) : undefined}
                  onClick={clickable ? () => onRowClick!(row) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick!(row);
                          }
                        }
                      : undefined
                  }
                  sx={clickable ? {
                    cursor: 'pointer',
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '-2px',
                    },
                  } : undefined}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align ?? 'left'} sx={{ py: 1.25 }}>
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {page !== undefined && pageCount !== undefined && onPageChange && pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={pageCount} page={page} color="primary"
            onChange={(_, value) => onPageChange(value)}
          />
        </Box>
      )}
    </Box>
  );
}

export default DataTable;
